/**
 * Sistema de recolección y exportación de logs para debugging
 */

interface LogEntry {
  timestamp: string;
  type: 'info' | 'error' | 'warn' | 'payload';
  message: string;
  data?: any;
}

class LogCollector {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private storageKey = 'debug_logs_collection';

  constructor() {
    // Interceptar console.log para capturar logs específicos
    this.interceptConsole();
    // Cargar logs previos si existen
    this.loadFromStorage();
  }

  private interceptConsole() {
    const originalLog = console.log;
    const self = this;

    console.log = function(...args: any[]) {
      // Llamar al console.log original
      originalLog.apply(console, args);

      // Capturar logs importantes
      const message = args.join(' ');
      
      // Filtrar logs que nos interesan
      if (
        // existing debug markers
        message.includes('Update payload') ||
        message.includes('PersonalForm -') ||
        message.includes('PersonalDetailModal -') ||
        message.includes('🔍 Intentando PUT') ||
        message.includes('🔍 Intentando POST') ||
        message.includes('✅ updateNombre') ||
        message.includes('✅ createNombre') ||
        message.includes('Invalidando queries') ||
        message.includes('Layout: Actualizando') ||
        message.includes('getPersonalById - campos') ||
        // planning-specific markers
        message.includes('[GUARDAR]') ||
        message.includes('[DELETE]') ||
        message.includes('[CARGA]') ||
        message.includes('Items a enviar') ||
        message.includes('Respuesta cartera') ||
        message.includes('Eliminando personas del backend') ||
        message.includes('Recargado desde backend') ||
        message.includes('Planificaciones desde backend') ||
        message.includes('Clientes base agregados') ||
        message.includes('Total de filas')
      ) {
        self.addLog('info', message, args.length > 1 ? args[1] : undefined);
      }
    };
  }

  // Permitir agregar un log manual desde otras partes de la app
  public addCustomLog(type: LogEntry['type'], message: string, data?: any) {
    this.addLog(type, message, data);
  }

  private addLog(type: LogEntry['type'], message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      type,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : undefined
    };

    this.logs.push(entry);

    // Mantener solo los últimos N logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Guardar en sessionStorage
    this.saveToStorage();
  }

  private saveToStorage() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Error guardando logs:', error);
    }
  }

  private loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error cargando logs:', error);
    }
  }

  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  public clearLogs() {
    this.logs = [];
    sessionStorage.removeItem(this.storageKey);
  }

  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  public downloadLogs() {
    const content = this.exportLogs();
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public printFilteredLogs(filter?: string) {
    const filtered = filter 
      ? this.logs.filter(log => log.message.toLowerCase().includes(filter.toLowerCase()))
      : this.logs;

    console.group('📋 Logs Capturados (' + filtered.length + ')');
    filtered.forEach(log => {
      console.log(`[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`);
      if (log.data) {
        console.log('  Data:', log.data);
      }
    });
    console.groupEnd();
  }
}

// Crear instancia global
export const logCollector = new LogCollector();

// Exponer en window para acceso desde consola
if (typeof window !== 'undefined') {
  (window as any).logCollector = logCollector;
  
  // Helper functions globales
  (window as any).viewLogs = () => logCollector.printFilteredLogs();
  (window as any).downloadLogs = () => logCollector.downloadLogs();
  (window as any).clearLogs = () => {
    logCollector.clearLogs();
    console.log('✅ Logs limpiados');
  };
  (window as any).filterLogs = (keyword: string) => logCollector.printFilteredLogs(keyword);
  
  console.log('🔧 Log Collector activado. Comandos disponibles:');
  console.log('  - viewLogs() - Ver todos los logs capturados');
  console.log('  - filterLogs("keyword") - Filtrar logs por palabra clave');
  console.log('  - downloadLogs() - Descargar logs como JSON');
  console.log('  - clearLogs() - Limpiar todos los logs');
}
